import ContactsService from '@/services/ContactsService'

const state = () => ({
  rows: [],
  meta: {
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0
  },
  loadingList: false,
  loadingOne: false,
  saving: false,
  deleting: false,
  error: null,
  current: null,
  notes: []
})

const getters = {
  rows: (s) => s.rows,
  meta: (s) => s.meta,
  loadingList: (s) => s.loadingList,
  loadingOne: (s) => s.loadingOne,
  saving: (s) => s.saving,
  deleting: (s) => s.deleting,
  error: (s) => s.error,
  current: (s) => s.current,
  notes: (s) => s.notes
}

const mutations = {
  SET_LOADING_LIST(s, v) { s.loadingList = v },
  SET_LOADING_ONE(s, v) { s.loadingOne = v },
  SET_SAVING(s, v) { s.saving = v },
  SET_DELETING(s, v) { s.deleting = v },
  SET_ERROR(s, v) { s.error = v },

  SET_LIST(s, paginator) {
    const p = paginator || {}
    s.rows = Array.isArray(p.data) ? p.data : []
    s.meta = {
      current_page: Number(p.current_page || 1),
      last_page: Number(p.last_page || 1),
      per_page: Number(p.per_page || 10),
      total: Number(p.total || 0)
    }
  },

  SET_CURRENT(s, contact) {
    s.current = contact || null
  },

  SET_NOTES(s, notes) {
    s.notes = Array.isArray(notes) ? notes : []
  },

  UPSERT_IN_LIST(s, contact) {
    if (!contact?.id) return
    const idx = s.rows.findIndex(r => r.id === contact.id)
    if (idx === -1) s.rows = [contact, ...s.rows]
    else s.rows.splice(idx, 1, contact)
  },

  REMOVE_FROM_LIST(s, id) {
    s.rows = s.rows.filter(r => r.id !== id)
  }
}

const actions = {
  async fetchList({ commit }, params = {}) {
    commit('SET_LOADING_LIST', true)
    commit('SET_ERROR', null)
    try {
      const res = await ContactsService.list(params)
      const paginator = res?.data ?? res
      commit('SET_LIST', paginator)
      return paginator
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to load contacts')
      throw e
    } finally {
      commit('SET_LOADING_LIST', false)
    }
  },

  async fetchOne({ commit }, { id, params } = {}) {
    if (!id) throw new Error('Missing id')
    commit('SET_LOADING_ONE', true)
    commit('SET_ERROR', null)
    try {
      const res = await ContactsService.get(id, params || {})
      const payload = res?.data ?? res
      commit('SET_CURRENT', payload?.contact ?? payload)
      commit('SET_NOTES', payload?.notes ?? [])
      return payload
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to load contact')
      throw e
    } finally {
      commit('SET_LOADING_ONE', false)
    }
  },

  async create({ commit }, payload) {
    commit('SET_SAVING', true)
    commit('SET_ERROR', null)
    try {
      const res = await ContactsService.create(payload)
      const contact = res?.data ?? res
      commit('UPSERT_IN_LIST', contact)
      commit('SET_CURRENT', contact)
      return contact
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to create contact')
      throw e
    } finally {
      commit('SET_SAVING', false)
    }
  },

  async update({ commit }, { id, payload }) {
    if (!id) throw new Error('Missing id')
    commit('SET_SAVING', true)
    commit('SET_ERROR', null)
    try {
      const res = await ContactsService.update(id, payload)
      const contact = res?.data ?? res
      commit('UPSERT_IN_LIST', contact)
      commit('SET_CURRENT', contact)
      return contact
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to update contact')
      throw e
    } finally {
      commit('SET_SAVING', false)
    }
  },

  async remove({ commit }, id) {
    if (!id) throw new Error('Missing id')
    commit('SET_DELETING', true)
    commit('SET_ERROR', null)
    try {
      await ContactsService.destroy(id)
      commit('REMOVE_FROM_LIST', id)
      return true
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to delete contact')
      throw e
    } finally {
      commit('SET_DELETING', false)
    }
  },

  async refreshNotes({ commit }, contactId) {
    if (!contactId) throw new Error('Missing contactId')
    commit('SET_ERROR', null)
    try {
      const res = await ContactsService.listNotes(contactId)
      const notes = res?.data ?? res
      commit('SET_NOTES', notes)
      return notes
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to load notes')
      throw e
    }
  },

  async addNote({ dispatch, commit }, { contactId, payload }) {
    if (!contactId) throw new Error('Missing contactId')
    commit('SET_ERROR', null)
    try {
      await ContactsService.addNote(contactId, payload)
      return await dispatch('refreshNotes', contactId)
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to add note')
      throw e
    }
  },

  async deleteNote({ dispatch, commit }, { contactId, noteId }) {
    if (!contactId || !noteId) throw new Error('Missing contactId or noteId')
    commit('SET_ERROR', null)
    try {
      await ContactsService.deleteNote(contactId, noteId)
      return await dispatch('refreshNotes', contactId)
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to delete note')
      throw e
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
